import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { EmptyState, Loading } from "@/components/shared";
import { Search, Building2, Globe, ExternalLink, Star, ShieldAlert, ShieldCheck, Info, AlertTriangle, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cap, uniq, countryLabel, REMOTE_LABEL, statusStyle, isStale, coverageState, fmtDate } from "@/lib/register";

function CoverageNotice({ jurisdiction, coverage }) {
  const cs = coverageState(jurisdiction, coverage);
  if (cs.state === "never") {
    return (
      <div data-testid="coverage-never" className="mt-6 flex items-start gap-2 rounded-[10px] border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>The {jurisdiction} register has never been swept. This is an absence of knowledge, not an absence of licensees: companies may be licensed here without appearing below.</span>
      </div>
    );
  }
  if (cs.state === "partial") {
    return (
      <div data-testid="coverage-partial" className="mt-6 flex items-start gap-2 rounded-[10px] border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>The {jurisdiction} register was only partially swept (last swept {fmtDate(cs.entry.last_swept_at)}). Absence from these results does not mean a company is unlicensed.</span>
      </div>
    );
  }
  if (cs.state === "full") {
    return (
      <div data-testid="coverage-full" className="mt-6 flex items-start gap-2 rounded-[10px] border border-teal-400/30 bg-teal-500/5 px-4 py-3 text-sm text-teal-700">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>The {jurisdiction} register was fully swept on {fmtDate(cs.entry.last_swept_at)}. A company not appearing here was not in the register at that time.</span>
      </div>
    );
  }
  return null;
}

function EmployerCard({ op, i }) {
  const lic = op.licences || [];
  const stale = lic.some((l) => l.verified && isStale(l.last_verified_at));
  const unconfirmed = lic.some((l) => !l.verified);
  const careers = op.careers_url || (op.extra_careers_urls || [])[0] || "";
  return (
    <article data-testid="licensee-card" className="card-surface lift flex h-full flex-col p-6 animate-fade-up" style={{ animationDelay: `${i * 25}ms` }}>
      <div className="flex items-start justify-between gap-3">
        <Link to={`/careers/${op.id}`} data-testid="licensee-detail-link" className="text-base font-semibold leading-snug text-ink transition-colors hover:text-teal-600">{op.employer_name}</Link>
        {op.company_type?.[0] && (
          <span className="shrink-0 rounded-full bg-[#E7F0EE] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600">{cap(op.company_type[0])}</span>
        )}
      </div>
      {op.brands?.length > 0 && <p className="mt-0.5 line-clamp-1 text-xs text-slate-500">{op.brands.join(" · ")}</p>}

      {lic.length === 0 ? (
        <p className="mt-3 flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="h-3.5 w-3.5 text-slate-500" /> No licences on record{op.hq_location?.location ? ` · ${op.hq_location.location}` : ""}
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {lic.map((l, idx) => {
            const st = statusStyle(l.licence_status);
            return (
              <span key={idx} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${st.alarm ? st.cls : "border-[#DDDAD1] text-slate-400"}`}>
                {st.alarm ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3 text-teal-400" />}
                {l.jurisdiction}{st.alarm ? ` · ${st.label}` : ""}
              </span>
            );
          })}
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
        {op.remote_policy && op.remote_policy !== "unknown" && <span>{REMOTE_LABEL[op.remote_policy]}</span>}
        {unconfirmed && <span className="text-slate-500">Some licences unconfirmed</span>}
        {stale && <span className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" /> Verification over 180 days old</span>}
      </div>

      <div className="mt-auto flex flex-wrap gap-2 pt-5">
        {careers && (
          <a href={careers} target="_blank" rel="noopener noreferrer" data-testid="licensee-careers-link" className="btn-teal inline-flex items-center gap-1.5 text-xs">
            Careers <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {op.corporate_site_url && (
          <a href={op.corporate_site_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#DDDAD1] px-3 py-2 text-xs font-semibold text-ink transition-colors hover:border-teal-400/50">
            <Globe className="h-3.5 w-3.5" /> Website
          </a>
        )}
        {op.reviews_company_id && (
          <Link to={`/company/${op.reviews_company_id}`} data-testid="licensee-reviews-link" className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#DDDAD1] px-3 py-2 text-xs font-semibold text-ink transition-colors hover:border-teal-400/50">
            <Star className="h-3.5 w-3.5 text-amber-500" /> Reviews
          </Link>
        )}
      </div>
    </article>
  );
}

export function CareersIndex() {
  const [q, setQ] = useState("");
  const [jurisdiction, setJurisdiction] = useState("all");
  const [companyType, setCompanyType] = useState("all");
  const [remotePolicy, setRemotePolicy] = useState("all");
  const [country, setCountry] = useState("all");
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ jurisdictions: [], company_types: [], remote_policies: [], country_codes: [], licence_statuses: [], jurisdiction_counts: {} });
  const [coverage, setCoverage] = useState(null);
  const debounce = useRef(null);

  const load = (o = {}) => {
    setItems(null);
    const val = (k, cur) => ((o[k] ?? cur) === "all" ? "" : (o[k] ?? cur));
    api.get("/licensees", { params: {
      q: o.q ?? q,
      jurisdiction: val("jurisdiction", jurisdiction),
      company_type: val("companyType", companyType),
      remote_policy: val("remotePolicy", remotePolicy),
      country_code: val("country", country),
      licence_status: val("status", status),
    } }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  };

  useEffect(() => {
    load();
    api.get("/licensee-filters").then(({ data }) => setFilters(data)).catch(() => {});
    api.get("/licensee-coverage").then(({ data }) => setCoverage(data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const onSearch = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load({ q: v }), 300);
  };

  const anyFilter = q || [jurisdiction, companyType, remotePolicy, country, status].some((x) => x !== "all");
  const neverSwept = jurisdiction !== "all" && coverageState(jurisdiction, coverage).state === "never";

  return (
    <div>
      <p className="mt-6 max-w-4xl text-slate-400">
        A read-only, derived register of licensed iGaming employers and their gambling licences, compiled from official sources. It is unofficial: each regulator's own published register is the authoritative record.
      </p>

      <div className="mt-8 flex items-center gap-2 rounded-full border border-[#DDDAD1] bg-ink-900 p-1.5 sm:max-w-xl">
        <Search className="ml-3 h-5 w-5 text-slate-400" aria-hidden="true" />
        <input data-testid="licensee-search" value={q} onChange={(e) => onSearch(e.target.value)} placeholder="Search employer, brand or alias…" className="min-w-0 flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-slate-500" />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Select value={jurisdiction} onValueChange={(v) => { setJurisdiction(v); load({ jurisdiction: v }); }}>
          <SelectTrigger data-testid="licensee-jurisdiction" className="w-52 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Jurisdiction" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All jurisdictions</SelectItem>
            {filters.jurisdictions.map((j) => (
              <SelectItem key={j} value={j}>{j}{filters.jurisdiction_counts?.[j] != null ? ` (${filters.jurisdiction_counts[j]})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={companyType} onValueChange={(v) => { setCompanyType(v); load({ companyType: v }); }}>
          <SelectTrigger data-testid="licensee-company-type" className="w-48 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Company type" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">All company types</SelectItem>
            {filters.company_types.map((t) => <SelectItem key={t} value={t}>{cap(t)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={(v) => { setStatus(v); load({ status: v }); }}>
          <SelectTrigger data-testid="licensee-status" className="w-44 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Licence status" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">Any licence status</SelectItem>
            {filters.licence_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={remotePolicy} onValueChange={(v) => { setRemotePolicy(v); load({ remotePolicy: v }); }}>
          <SelectTrigger data-testid="licensee-remote" className="w-44 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Remote policy" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">Any remote policy</SelectItem>
            {filters.remote_policies.map((r) => <SelectItem key={r} value={r}>{REMOTE_LABEL[r] || cap(r)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={country} onValueChange={(v) => { setCountry(v); load({ country: v }); }}>
          <SelectTrigger data-testid="licensee-country" className="w-48 border-[#DDDAD1] bg-ink-900"><SelectValue placeholder="Hiring country" /></SelectTrigger>
          <SelectContent className="border-[#DDDAD1] bg-ink-900">
            <SelectItem value="all">Any hiring country</SelectItem>
            {filters.country_codes.map((c) => <SelectItem key={c} value={c}>{countryLabel(c)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {jurisdiction !== "all" && <CoverageNotice jurisdiction={jurisdiction} coverage={coverage} />}

      {items === null ? (
        <Loading label="Loading register" />
      ) : items.length === 0 ? (
        neverSwept ? (
          <div data-testid="empty-never-swept" className="mt-10 rounded-[12px] border border-amber-500/40 bg-amber-500/5 p-8 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-600" />
            <h3 className="mt-3 text-lg font-semibold text-ink">No register data for {jurisdiction} yet</h3>
            <p className="mx-auto mt-2 max-w-lg text-sm text-slate-400">The {jurisdiction} register has never been swept, so we hold no records for it. This does not mean no companies are licensed there: it means we have not gathered that data yet.</p>
          </div>
        ) : (
          <div className="mt-10">
            <EmptyState icon={Building2} title="No employers match" subtitle="Nothing matched these filters. Try clearing or widening them." />
          </div>
        )
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((op, i) => <EmployerCard key={op.id} op={op} i={i} />)}
        </div>
      )}
    </div>
  );
}
